import {Card, Container, createStyles, Group, Menu, rem, Space, Tabs, Text, Title, UnstyledButton} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren} from "react";
import {SiteEntity} from "@ots-frontend/ots-data";
import {SiteDetails} from "./SiteDetails";
import {SiteAssignments} from "./SiteAssignments";
import {useTranslation} from "react-i18next";
import {SiteActionMenuDropdown} from "../../menu/SiteActionMenu/SiteActionMenu";
import {ObjectChangeLogView} from "../common/ObjectChangeLogView/ObjectChangeLogView";
import {SiteMileage} from "./SiteMileage";
import {TabItem} from "../../tabs/Tabs/Tabs";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {SiteInsights} from "./SiteInsights";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {EntityNotesView} from "../common/EntityNotesView/EntityNotesView";

const useStyles = createStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type SiteProfileProps = PropsWithChildren<{
  site: SiteEntity
}>

const tabs: TabItem[] = [
  {
    key: "details",
    value: "Details"
  },
  {
    key: 'notes',
    value: 'Notes'
  },
  {
    key: "insights",
    value: "Insights",
  },
  {
    key: "assignments",
    value: "Assignments",
    permissions: ["site_assignment_view"]
  },
  {
    key: "mileage",
    value: "Mileage Table",
    permissions: ["sitesiteassoc_view"]
  },
  {
    key: "history",
    value: "Change Logs",
    permissions: ["change_log_view"]
  },
];

export const SiteProfile = (props: SiteProfileProps) => {
  const {site} = props;

  const {classes} = useStyles();

  const {regionId, appCategory} = useApp()
  const {t} = useTranslation()
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>Site: {site.name}</Title>
          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <SiteActionMenuDropdown site={site}/>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={(value) => navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <SiteDetails site={site}/>
            </Tabs.Panel>
            <Tabs.Panel value="notes">
              <EntityNotesView entity={site}/>
            </Tabs.Panel>
            <Tabs.Panel value="insights">
              <SiteInsights site={site} category={appCategory} regionId={regionId}/>
            </Tabs.Panel>
            <Tabs.Panel value="assignments">
              <SiteAssignments site={site}/>
            </Tabs.Panel>
            <Tabs.Panel value="mileage">
              <SiteMileage site={site}/>
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <ObjectChangeLogView entity={site}/>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
